import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  getDefaultRoute,
  isValidUserRole,
  hasRouteAccess,
  UserRole,
} from "../helpers/permissions";
import { getUser } from "../helpers/utils";
import { UserProvider, useUser } from "./auth/context/user-context";
import { useAutoTutorial } from "../hooks/useAutoTutorial";

import { AssistantSparkCluster } from "../components/AssistantSparkCluster";
import { ButtonTooltip } from "../components/tooltip";
import { useLoginCounter, SESSION_KEY_LOGIN_INCREMENTED } from "../helpers/hooks";
import { useApp } from "../helpers/app-context";
import { useChatV2Enabled } from "../hooks/useChatV2Enabled";
import { ChatV2 } from "../chatv2";
import { Chatbot } from "./chatbot/Chatbot";
import { GeneralFeedbackDialog } from "../components/feedback/GeneralFeedbackDialog";
import { FirstBookingCongratsModal } from "../components/FirstBookingCongratsModal";
import { useFirstBookingCongrats } from "../hooks/useFirstBookingCongrats";
import { MinFeeEarlyWarningModal } from "../components/MinFeeEarlyWarningModal";
import { useMinFeeEarlyWarning } from "../hooks/useMinFeeEarlyWarning";
import { OPEN_INSIGHTS_EVENT } from "../constant";
import { WelcomeTourCard } from "../components/Tutorial/WelcomeTourCard";

// Early permission check wrapper - checks BEFORE UserProvider loads
function PermissionCheckWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const currentPath = location.pathname;
  const [shouldRender, setShouldRender] = useState(true);

  useEffect(() => {
    // Check if user is authenticated first
    const token = localStorage.getItem("token");

    // If not authenticated, let the children render (UserProvider will handle auth redirect)
    if (!token || !user || !user.id) {
      setShouldRender(true);
      return;
    }

    // Skip permission check for root and onboarding
    if (currentPath === "/" || currentPath === "/onboarding") {
      setShouldRender(true);
      return;
    }

    const userRole = user.type as UserRole;

    // Check if user has access to current route
    if (isValidUserRole(userRole)) {
      const hasAccess = hasRouteAccess(userRole, currentPath);

      if (!hasAccess) {
        const defaultRoute = getDefaultRoute(userRole);
        setShouldRender(false);
        navigate(defaultRoute, { replace: true });
        return;
      }
    }

    setShouldRender(true);
  }, [currentPath, user?.id, user?.type, navigate]);

  // Don't render children if we're redirecting
  if (!shouldRender) {
    return null;
  }

  // User has access, render children
  return <>{children}</>;
}

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const user = getUser();
  const { onboardingData } = useUser() as any;
  const { isChatBotOpen, setIsChatBotOpen } = useApp() as {
    isChatBotOpen: boolean;
    setIsChatBotOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  const isChatV2Enabled = useChatV2Enabled();
  const isMarketingUser = user?.type === "MarketingOnly";
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const toggleChatExpanded = useCallback(() => setIsChatExpanded((prev) => !prev), []);
  // Desktop "full" mode: chat takes the whole row and the page hides. Compact
  // mode keeps the page visible and just shrinks it (CoPilot sidebar).
  const isFullExpanded = isChatV2Enabled && isDesktop && isChatExpanded && isChatBotOpen;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Apply the refined-teal dashboard token scope to the document body so all
  // logged-in pages (Marketing, Settings, etc.) and any portal-rendered
  // modals inherit the `.d-*` styles (e.g. `.d-ctrl-pill` used by the shared
  // YearFilter and by FilterSelect inside the PacingReportModal portal).
  // Login/register pages are outside MainApp so they remain in the legacy look.
  useEffect(() => {
    document.body.setAttribute("data-design", "refined-teal");
    return () => document.body.removeAttribute("data-design");
  }, []);

  useEffect(() => {
    setIsChatExpanded(false);
  }, [location.pathname]);

  // Esc closes the chat (any mode). Previously handled inside ChatHost; the
  // chat is now an in-flow sidebar, so we own the shortcut here. Bound only
  // while open so we don't hijack Esc otherwise.
  useEffect(() => {
    if (!isChatBotOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsChatBotOpen(false);
        setIsChatExpanded(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isChatBotOpen, setIsChatBotOpen]);

  // Offer the welcome tour on first 3 logins, for both customer types
  const { showWelcomePrompt, closeWelcomePrompt, dismissForever } =
    useAutoTutorial(onboardingData);

  const { shouldShowFeedback, dismissFeedback, incrementLoginCount } = useLoginCounter();
  const {
    open: showFirstBookingCongrats,
    dismiss: dismissFirstBookingCongrats,
    sentAt: firstBookingSentAt,
  } = useFirstBookingCongrats();
  const { open: showMinFeeEarlyWarning, dismiss: dismissMinFeeEarlyWarning } =
    useMinFeeEarlyWarning();
  const feedbackDialogOpen =
    shouldShowFeedback && onboardingData?.isOnboardingCompleted === true;
  // Every mounted Dialog registers its own document-level Escape listener,
  // so stacking the congrats popup under the tour/feedback dialogs would let
  // one Escape dismiss (and permanently latch) it unseen. Defer it until the
  // other dialogs are gone — it re-evaluates the moment they close.
  const firstBookingCongratsVisible =
    showFirstBookingCongrats && !showWelcomePrompt && !feedbackDialogOpen;
  // Same deferral, plus: congrats wins if both one-time popups are eligible.
  // Re-evaluates the moment the congrats popup is dismissed.
  const minFeeEarlyWarningVisible =
    showMinFeeEarlyWarning &&
    !showWelcomePrompt &&
    !feedbackDialogOpen &&
    !showFirstBookingCongrats;
  useEffect(() => {
    const key = SESSION_KEY_LOGIN_INCREMENTED;
    if (sessionStorage.getItem(key)) return;
    const token = localStorage.getItem('token');
    if (!token || !user?.id) return;
    incrementLoginCount();

    // Track login count for the welcome-tour auto-offer (all roles). Runs
    // once per browser session for every authenticated entry path — login
    // form, signup/onboarding, and emailed auto-login links. Manual property
    // switches reuse the same tab session, so the flag above intentionally
    // skips them (matching incrementLoginCount); the switched-to identity
    // gets counted on its next fresh sign-in.
    const loginCountKey = `tutorial_login_count_${user.id}`;
    const count = parseInt(localStorage.getItem(loginCountKey) || "0", 10);
    localStorage.setItem(loginCountKey, String(count + 1));

    sessionStorage.setItem(key, 'true');
  }, [incrementLoginCount, user?.id]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token || !user || !user.id) {
      navigate("/login", {
        state: { from: `${location.pathname}${location.search}` },
      });
      setIsLoggedIn(false);
      return;
    }

    const userRole = user.type;

    // User is authenticated, set logged in state
    setIsLoggedIn(true);

    // Wait for onboarding data to load
    if (onboardingData === undefined) {
      return; // Still loading onboarding data
    }

    // Handle onboarding completion and root path redirect
    if (!onboardingData.isOnboardingCompleted) {
      // Redirect to onboarding if not completed
      navigate("/onboarding");
      return;
    }

    // V2 users: Also check if dashboard is unlocked
    // dashboardUnlocked is set to true when ads go live (impressions > 0)
    const isV2 = onboardingData.userMetadata?.onboardingVersion === "v2";
    if (isV2 && onboardingData.dashboardUnlocked !== true) {
      // Dashboard not yet unlocked - redirect to onboarding (shows waiting screen)
      navigate("/onboarding");
      return;
    }

    // Guard: an already-live user must never sit in the onboarding flow.
    // This is the inverse of the two gates above (so they can never disagree
    // and loop): a completed v1 user, or a completed v2 user whose dashboard
    // is unlocked, is "live" and is redirected out to their role's home.
    // Note: add-property / continue-later re-entry is unaffected because those
    // flows swap the session into a fresh, still-incomplete property.
    const isLiveUser =
      onboardingData.isOnboardingCompleted &&
      (!isV2 || onboardingData.dashboardUnlocked === true);
    if (isLiveUser && location.pathname.startsWith("/onboarding")) {
      navigate(getDefaultRoute(userRole));
      return;
    }

    // Handle root path redirect with role-based navigation
    if (location.pathname === "/") {
      if (isValidUserRole(userRole)) {
        const defaultRoute = getDefaultRoute(userRole);
        navigate(defaultRoute);
      } else {
        // Fallback to dashboard for invalid roles
        navigate("/dashboard");
      }
    }
  }, [
    onboardingData,
    location.pathname,
    location.search,
    navigate,
    user?.id,
    user?.type,
  ]);

  // Covers both `/onboarding` and `/onboarding/:session_id`. Used to suppress the
  // chatbot launcher (it has no onboarding context). The GoLiveBanner is now
  // rendered inside Page.tsx — see the comment at its render site there.
  const isOnboardingPage = location.pathname.startsWith("/onboarding");

  if (!isLoggedIn) {
    return null;
  }

  const handleCloseChatBot = () => {
    setIsChatBotOpen(false);
    setIsChatExpanded(false);
  };

  const chatNode = isChatV2Enabled ? (
    <ChatV2
      isDesktop={isDesktop}
      onClose={handleCloseChatBot}
      expanded={isChatExpanded}
      toggleExpanded={toggleChatExpanded}
    />
  ) : (
    <Chatbot />
  );

  return (
    <div className="w-full flex flex-col z-[1] h-screen">
      <div className="w-full flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Page content. It stays IN FLOW and simply shrinks as the chat
            sidebar pushes in from the right — the "CoPilot" layout: no
            overlay, no dim/blur, the page underneath stays fully live.
            In desktop "full expanded" mode the page hides so the assistant
            gets the whole row. `contents` keeps the wrapper layout-neutral
            so the page's own root participates in the flex row directly. */}
        <div className={isFullExpanded ? "hidden" : "contents"}>
          <Outlet />
        </div>

        {/* Assistant — a SINGLE ChatV2 instance, never rendered twice.
            Desktop chat-v2: in-flow sibling that pushes the page left
            (compact 420px rail, or full row when expanded). Mobile + the
            legacy v1 Chatbot draw their own internal full-screen panel, so
            they just mount inline. */}
        {!isOnboardingPage && isChatBotOpen &&
          (isChatV2Enabled && isDesktop ? (
            <motion.div
              key="chat-inline"
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className={
                isFullExpanded
                  ? "flex-1 min-w-0 h-full p-3"
                  : "flex-shrink-0 h-full py-3 pr-3"
              }
            >
              {/* Inner box owns the panel's real size: full width in expanded
                  mode, a fixed 420px rail pinned to the right edge otherwise.
                  ChatV2's panel is `w-full h-full`, so it fills this box. */}
              <div
                className={
                  isFullExpanded ? "w-full h-full" : "w-[350px] h-full ml-auto"
                }
              >
                {chatNode}
              </div>
            </motion.div>
          ) : (
            <div className="flex-shrink-0">
              {chatNode}
            </div>
          ))}

        {!isOnboardingPage && !isChatBotOpen && (
          // `.a-launcher` from the Claude-Design Reputation mock — ink
          // gradient pill with a cream + amber sparkle cluster animated
          // by the kit's `a-spark-pulse` keyframe (small spark offset
          // by 0.9s). Hover shortens duration to 1.6s + lifts -2px.
          // `data-design="refined-teal"` keeps the kit classes resolving
          // when the launcher mounts outside the dashboard scope.
          // Desktop-only FAB — on phones the assistant lives in MobileHeader.
          <div className="hidden lg:block">
          <button
            data-tutorial="ai-copilot-button"
            type="button"
            onClick={() => {
              // Following the congrats popup's pointer counts as engaging
              // with it — dismiss (latches) before opening the chat.
              if (firstBookingCongratsVisible) dismissFirstBookingCongrats();
              setIsChatBotOpen((p) => !p);
            }}
            className="a-launcher"
            // Lift the launcher above the congrats dialog's z-[70] backdrop
            // (its own stylesheet z-index is 30) so the pointer aims at a
            // fully-lit, clickable target.
            style={firstBookingCongratsVisible ? { zIndex: 71 } : undefined}
            data-design="refined-teal"
            aria-label="Open Autumn assistant"
          >
            <AssistantSparkCluster />
          </button>
          </div>
        )}
      </div>
      <ButtonTooltip />
      <GeneralFeedbackDialog
        open={feedbackDialogOpen}
        onClose={dismissFeedback}
      />
      <FirstBookingCongratsModal
        open={firstBookingCongratsVisible}
        onClose={dismissFirstBookingCongrats}
        sentAt={firstBookingSentAt}
        onAskAssistant={() => {
          dismissFirstBookingCongrats();
          setIsChatBotOpen(true);
        }}
        onOpenInsights={() => {
          // Opens the "Insights" pill's Recent-activity panel in the
          // marketing topbar (LiveActivityToggle listens for this event).
          // No-op on routes where the pill isn't mounted.
          window.dispatchEvent(new CustomEvent(OPEN_INSIGHTS_EVENT));
        }}
      />
      <MinFeeEarlyWarningModal
        open={minFeeEarlyWarningVisible}
        onClose={dismissMinFeeEarlyWarning}
      />
      <WelcomeTourCard
        open={showWelcomePrompt}
        onClose={closeWelcomePrompt}
        onDismissForever={dismissForever}
      />
    </div>
  );
}

export function Main() {
  return (
    <PermissionCheckWrapper>
      <UserProvider>
        <MainApp />
      </UserProvider>
    </PermissionCheckWrapper>
  );
}
